import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const KEY = 'inkphd_cookie_notice';
const VERSION = '2026.06';

/**
 * Cookie notice. InkPhD sets no advertising or analytics storage, so this is an
 * informed-notice banner (ePrivacy Art. 5(3) strictly-necessary exemption) with
 * an explicit acknowledgement recorded locally, plus a link to the full policy.
 */
export default function CookieConsent() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(KEY) || 'null');
      if (!saved || saved.version !== VERSION) setOpen(true);
    } catch (e) { setOpen(true); }
  }, []);

  const accept = () => {
    try {
      localStorage.setItem(KEY, JSON.stringify({
        version: VERSION, acknowledged_at: new Date().toISOString(),
        scope: 'strictly-necessary-only',
      }));
    } catch (e) { /* private mode */ }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[60] p-3 sm:p-4" data-testid="cookie-consent">
      <div className="max-w-4xl mx-auto rounded-xl border border-border bg-background/95 backdrop-blur shadow-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <Cookie className="w-5 h-5 text-[#1d4ed8] shrink-0" />
        <p className="text-[12px] leading-relaxed text-foreground/85 flex-1">
          InkPhD stores only what is strictly necessary to run your session and remember your
          workspace preferences. <strong>No advertising, no analytics, no cross-site tracking.</strong>{' '}
          <Link to="/legal/cookies" className="underline hover:text-[#1d4ed8]"
            data-testid="cookie-consent-policy-link">Cookie Policy</Link>
          {' · '}
          <Link to="/legal/privacy" className="underline hover:text-[#1d4ed8]"
            data-testid="cookie-consent-privacy-link">Privacy</Link>
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" onClick={accept} data-testid="cookie-consent-accept"
            className="bg-[#0b1a35] hover:bg-[#1d4ed8] text-white">
            Understood
          </Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={accept}
            aria-label="Dismiss" data-testid="cookie-consent-dismiss">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
